import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { TransactionTypesProvider } from './contexts/TransactionTypesContext'
import { PetsProvider } from './contexts/PetsContext'
import useSEO from './hooks/useSEO'

// Layout e Páginas
import Layout from './components/Layout/Layout'
import HomeWrapper from './pages/HomeWrapper'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Pets from './pages/Pets'
import Agenda from './pages/Agenda'
import Transactions from './pages/Transactions'
import Users from './pages/Users'
import PetDetails from './pages/PetDetails'
import Blog from './pages/Blog'
import BlogPost from './pages/BlogPost'
import NotFound from './pages/NotFound'
import PlanExpirationChecker from './components/PlanExpirationChecker'

/** Detecta se o acesso é de um crawler (Google, Bing, etc.) para evitar redirect e devolver 200 + noindex */
const isCrawler = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return false
  const ua = navigator.userAgent.toLowerCase()
  return /googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|twitterbot|rogerbot|linkedinbot|embedly|quora link preview|showyoubot|outbrain|pinterest|slackbot|vkshare|w3c_validator|whatsapp|crawler|spider|bot/i.test(ua)
}

/** Página mínima para crawlers em rotas privadas: 200 OK + noindex (evita "Página com redirecionamento") */
const RestrictedForCrawler = () => {
  useSEO({ title: 'Área restrita', noindex: true })
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
      <p className="text-muted-light dark:text-muted-dark">Área restrita. Faça login para acessar.</p>
    </div>
  )
}

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light dark:border-primary-dark mx-auto mb-4"></div>
          <p className="text-muted-light dark:text-muted-dark">Carregando...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    if (isCrawler()) {
      return <RestrictedForCrawler />
    }
    return <Navigate to="/login" replace />
  }

  return children
}

const PrivateLayout = () => (
  <RequireAuth>
    <Layout>
      <Outlet />
    </Layout>
  </RequireAuth>
)

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TransactionTypesProvider>
          <PetsProvider>
            <NotificationProvider>
              <PlanExpirationChecker />
              <BrowserRouter>
                <Routes>
                {/* Públicas */}
                <Route path="/" element={<HomeWrapper />} />
                <Route path="/login" element={<Login />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:id" element={<BlogPost />} />

                {/* Privadas (com Layout) */}
                <Route element={<PrivateLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/pets" element={<Pets />} />
                  <Route path="/pets/:id" element={<PetDetails />} />
                  <Route path="/agenda" element={<Agenda />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/users" element={<Users />} />
                </Route>

                {/* Fallback - Página 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            </NotificationProvider>
          </PetsProvider>
        </TransactionTypesProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App


